import { Check, ChevronRight, Tag } from 'lucide-react';
import { PlanDetails, ServiceType, PlanItem } from '../../types/api';
import { useTranslation } from 'react-i18next';
import { FEATURES } from '../../config';

// Create a function to generate config with current prices
const createSubscriptionConfig = (planDetails: PlanItem[] | null, serviceType: ServiceType, t: any) => {
  // Find current plans from API
  const standardPlan = planDetails?.find(plan => 
    plan.type === 'standard' && (!plan.service_type || plan.service_type === serviceType)
  );
  
  const premiumPlan = planDetails?.find(plan => 
    plan.type === 'premium' && (!plan.service_type || plan.service_type === serviceType)
  );

  // Fallback prices (only used if API is completely unavailable)
  // TODO: Replace with configService.getFallbackPricing() when component structure allows async operations
  const fallbackPrices = {
    flower: { standard: 399, premium: 599, standardOriginal: 599, premiumOriginal: 999 },
    fruit: { standard: 999, premium: 1499, standardOriginal: 1499, premiumOriginal: 2249 },
    cat_grass: { standard: 249, premium: 349, standardOriginal: 349, premiumOriginal: 499 }
  };

  const fallback = fallbackPrices[serviceType];

  return {
    flower: {
      namespace: 'flowers',
      backgroundColor: 'bg-gradient-to-b from-yellow-100 to-yellow-200',
      title: t('flowers.plansSection.title'),
      subtitle: t('flowers.plansSection.subtitle'),
      priceColor: 'text-amber-500',
      plans: {
        standard: {
          badge: { text: t('flowers.plansSection.badges.popularChoice'), bgColor: 'bg-orange-100', textColor: 'text-orange-600' },
          title: t('flowers.plans.standard.title'),
          description: t('flowers.plansSection.standardDescription'),
          price: serviceType === 'flower' ? (standardPlan?.price || fallback.standard) : (standardPlan?.price || fallback.standard),
          originalPrice: serviceType === 'flower' ? (standardPlan?.original_price || fallback.standardOriginal) : (standardPlan?.original_price || fallback.standardOriginal),
          buttonStyle: 'bg-white hover:bg-gray-50 text-black',
          features: [
            t('flowers.plansSection.features.weeklyFreshFlowers'),
            t('flowers.plansSection.features.freeDelivery'),
            t('flowers.plansSection.features.basicArrangement')
          ]
        },
        premium: {
          badge: { text: t('flowers.plansSection.badges.bestValue'), bgColor: 'bg-blue-100', textColor: 'text-blue-600' },
          title: t('flowers.plans.premium.title'),
          description: t('flowers.plansSection.premiumDescription'),
          price: serviceType === 'flower' ? (premiumPlan?.price || fallback.premium) : (premiumPlan?.price || fallback.premium),
          originalPrice: serviceType === 'flower' ? (premiumPlan?.original_price || fallback.premiumOriginal) : (premiumPlan?.original_price || fallback.premiumOriginal),
          buttonStyle: 'bg-gradient-to-r from-yellow-400 to-amber-400 hover:from-yellow-500 hover:to-amber-500 text-black',
          features: [
            t('flowers.plansSection.features.weeklyPremiumFlowers'),
            t('flowers.plansSection.features.freePriorityDelivery'),
            t('flowers.plansSection.features.premiumArrangement'),
            t('flowers.plansSection.features.prioritySupport')
          ]
        }
      }
    },
    fruit: {
      namespace: 'fruits',
      backgroundColor: 'bg-gradient-to-b from-amber-100 to-orange-100',
      title: t('fruits.plansSection.title'),
      subtitle: t('fruits.plansSection.subtitle'),
      priceColor: 'text-orange-400',
      plans: {
        standard: {
          badge: { text: t('fruits.plansSection.badges.popularChoice'), bgColor: 'bg-orange-100', textColor: 'text-orange-600' },
          title: t('fruits.plans.standard.title'),
          description: t('fruits.plansSection.standardDescription'),
          price: standardPlan?.price || fallback.standard,
          originalPrice: standardPlan?.original_price || fallback.standardOriginal,
          buttonStyle: 'bg-white hover:bg-gray-50 text-black',
          features: [
            t('fruits.plansSection.features.weeklyFreshFruits'),
            t('fruits.plansSection.features.freeDelivery'),
            t('fruits.plansSection.features.flexibleScheduling')
          ]
        },
        premium: {
          badge: { text: t('fruits.plansSection.badges.bestValue'), bgColor: 'bg-blue-100', textColor: 'text-blue-600' },
          title: t('fruits.plans.premium.title'),
          description: t('fruits.plansSection.premiumDescription'),
          price: premiumPlan?.price || fallback.premium,
          originalPrice: premiumPlan?.original_price || fallback.premiumOriginal,
          buttonStyle: 'bg-orange-400 text-white',
          features: [
            t('fruits.plansSection.features.weeklyPremiumFruits'),
            t('fruits.plansSection.features.freePriorityDelivery'),
            t('fruits.plansSection.features.premiumFruitVarieties'),
            t('fruits.plansSection.features.priorityScheduling')
          ]
        }
      }
    },
    cat_grass: {
      namespace: 'catGrass',
      backgroundColor: 'bg-gradient-to-b from-green-100 to-emerald-100',
      title: t('catGrass.plansSection.title'),
      subtitle: t('catGrass.plansSection.subtitle', 'Choose the perfect cat grass plan for your feline friends'),
      priceColor: 'text-green-500',
      plans: {
        standard: {
          badge: { text: t('catGrass.plansSection.badges.popularChoice', 'Popular Choice'), bgColor: 'bg-green-100', textColor: 'text-green-600' },
          title: t('catGrass.plansSection.standardPlan'),
          description: t('catGrass.plansSection.standardDescription'),
          price: standardPlan?.price || fallback.standard,
          originalPrice: standardPlan?.original_price || fallback.standardOriginal,
          buttonStyle: 'bg-white hover:bg-gray-50 text-black',
          features: [
            t('catGrass.plansSection.features.weeklyFreshCatGrass'),
            t('catGrass.plansSection.features.freeDelivery'),
            t('catGrass.plansSection.features.flexibleScheduling')
          ]
        },
        premium: {
          badge: { text: t('catGrass.plansSection.badges.bestValue', 'Best Value'), bgColor: 'bg-blue-100', textColor: 'text-blue-600' },
          title: t('catGrass.plansSection.premiumPlan'),
          description: t('catGrass.plansSection.premiumDescription'),
          price: premiumPlan?.price || fallback.premium,
          originalPrice: premiumPlan?.original_price || fallback.premiumOriginal,
          buttonStyle: 'bg-green-500 text-white',
          features: [
            t('catGrass.plansSection.features.weeklyFreshCatGrass'),
            t('catGrass.plansSection.features.freePriorityDelivery'),
            t('catGrass.plansSection.features.priorityScheduling')
          ]
        }
      }
    }
  };
};

interface SubscriptionPlansSectionProps {
  isLoading: boolean;
  planDetails: PlanItem[] | null; // Updated to match actual API response format
  handlePlanSelect: (plan: 'standard' | 'premium') => void;
  promoInfo?: string | null;
  serviceType?: ServiceType;
}

export const SubscriptionPlansSection = ({ 
  isLoading, 
  planDetails, 
  handlePlanSelect,
  promoInfo,
  serviceType = 'flower'
}: SubscriptionPlansSectionProps) => {
  const { t } = useTranslation();
  
  // Get dynamic configuration with current API prices
  const SUBSCRIPTION_CONFIG = createSubscriptionConfig(planDetails, serviceType, t);
  const config = SUBSCRIPTION_CONFIG[serviceType];
  
  // Now we can remove the manual price logic since config already has the right prices
  const standardPrice = config.plans.standard.price;
  const premiumPrice = config.plans.premium.price;
  const originalStandardPrice = config.plans.standard.originalPrice;
  const originalPremiumPrice = config.plans.premium.originalPrice;

  return (
    <div className={`min-h-screen px-6 py-14 flex items-center justify-center ${config.backgroundColor}`}>
      <section 
        id="plans-panel"
        role="region"
        aria-labelledby="plans-heading"
        aria-label={t('common.subscriptionPlans')}
        className="max-w-sm w-full"
      >
        {/* Main heading section */}
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-gray-800 leading-tight mb-6">
            {config.title}
          </h1>
          <p className="text-gray-600 text-xl mx-6">
            {config.subtitle}
          </p>
        </div>
        
        {isLoading ? (
          <div className="flex justify-center items-center py-16">
            <div className="animate-spin rounded-full h-12 w-12 border-t-4 border-b-4 border-orange-500"></div>
            <p className="mt-4 text-orange-700">{t('common.loadingPlans')}</p>
          </div>
        ) : (
          <div className="space-y-8">
            {/* Standard Plan Card */}
            <div className="bg-white rounded-3xl p-6 shadow-lg">
              {/* Popular choice badge */}
              <div className="flex items-center mb-5">
                <span className={`${config.plans.standard.badge.bgColor} ${config.plans.standard.badge.textColor} text-xs px-3 py-2 rounded-full font-medium flex items-center`}>
                  {config.plans.standard.badge.text}
                </span>
              </div>
              
              <h3 className="text-3xl font-bold text-black mb-3">{config.plans.standard.title}</h3>
              <p className="text-gray-600 text-base mb-6">
                {config.plans.standard.description}
              </p>
              
              {/* Pricing */}
              <div className="mb-6">
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-gray-400 line-through text-sm">{t('common.currency')} {originalStandardPrice}</span>
                  <span className="text-gray-400 text-sm">/ {t('common.month')}</span>
                </div>
                <div className="flex items-baseline gap-2">
                  <span className="text-2xl font-bold text-black">{t('common.currency')}</span>
                  <span className="text-5xl font-bold text-black">{standardPrice}</span>
                  <span className="text-gray-500 text-sm">/ {t('common.month')}</span>
                </div>
              </div>
              
              {/* Choose plan button */}
              <div className="relative w-full mb-6">
                <button 
                  onClick={() => !FEATURES.ENABLE_SUBSCRIPTION_PURCHASES ? null : handlePlanSelect('standard')}
                  className={`w-full ${FEATURES.ENABLE_SUBSCRIPTION_PURCHASES ? config.plans.standard.buttonStyle : 'bg-gray-300 text-gray-500 cursor-not-allowed'} font-semibold py-4 px-6 rounded-full transition-colors`}
                  disabled={!FEATURES.ENABLE_SUBSCRIPTION_PURCHASES}
                >
                  {FEATURES.ENABLE_SUBSCRIPTION_PURCHASES ? t('common.choosePlan') : t('common.serviceUnavailable')}
                </button>
                
                {/* Promotional badge */}
                {promoInfo && (
                  <div className="absolute -top-2 -right-2 bg-red-500 text-white text-xs px-3 py-1 rounded-full font-medium shadow-lg z-20">
                    {promoInfo}
                  </div>
                )}
              </div>
              
              {/* Divider */}
              <div className="border-t border-gray-200 mb-6"></div>
              
              {/* Features */}
              <ul className="space-y-5">
                {config.plans.standard.features.map((feature, index) => (
                  <li key={index} className="flex items-center gap-3">
                    <div className="w-5 h-5 bg-green-500 rounded-full flex items-center justify-center flex-shrink-0">
                      <Check size={12} className="text-white" />
                    </div>
                    <span className="text-gray-700 text-sm">{feature}</span>
                  </li>
                ))}
              </ul>
            </div>
            
            {/* Premium Plan Card */}
            <div className="bg-white rounded-3xl p-6 shadow-lg">
              {/* Best value badge */}
              <div className="flex items-center mb-5">
                <span className={`${config.plans.premium.badge.bgColor} ${config.plans.premium.badge.textColor} text-xs px-3 py-2 rounded-full font-medium flex items-center`}>
                  {config.plans.premium.badge.text}
                </span>
              </div>
              
              <h3 className="text-3xl font-bold text-black mb-3">{config.plans.premium.title}</h3>
              <p className="text-gray-600 text-base mb-6">
                {config.plans.premium.description}
              </p>
              
              {/* Pricing */}
              <div className="mb-6">
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-gray-400 line-through text-sm">{t('common.currency')} {originalPremiumPrice}</span>
                  <span className="text-gray-400 text-sm">/ {t('common.month')}</span>
                </div>
                <div className="flex items-baseline gap-2">
                  <span className={`text-2xl font-bold ${config.priceColor}`}>{t('common.currency')}</span>
                  <span className={`text-5xl font-bold ${config.priceColor}`}>{premiumPrice}</span>
                  <span className="text-gray-500 text-sm">/ {t('common.month')}</span>
                </div>
              </div>
              
              {/* Choose plan button */}
              <div className="relative w-full mb-6">
                <button 
                  onClick={() => !FEATURES.ENABLE_SUBSCRIPTION_PURCHASES ? null : handlePlanSelect('premium')}
                  className={`w-full ${FEATURES.ENABLE_SUBSCRIPTION_PURCHASES ? config.plans.premium.buttonStyle : 'bg-gray-300 text-gray-500 cursor-not-allowed'} font-medium py-4 px-6 rounded-full transition-colors`}
                  disabled={!FEATURES.ENABLE_SUBSCRIPTION_PURCHASES}
                >
                  {FEATURES.ENABLE_SUBSCRIPTION_PURCHASES ? t('common.choosePlan') : t('common.serviceUnavailable')}
                </button>
                
                {/* Promotional badge */}
                {promoInfo && (
                  <div className="absolute -top-2 -right-2 bg-red-500 text-white text-xs px-3 py-1 rounded-full font-medium shadow-lg z-20">
                    {promoInfo}
                  </div>
                )}
              </div>
              
              {/* Divider */}
              <div className="border-t border-gray-200 mb-6"></div>
              
              {/* Features */}
              <ul className="space-y-5">
                {config.plans.premium.features.map((feature, index) => (
                  <li key={index} className="flex items-center gap-3">
                    <div className="w-5 h-5 bg-green-500 rounded-full flex items-center justify-center flex-shrink-0">
                      <Check size={12} className="text-white" />
                    </div>
                    <span className="text-gray-700 text-sm">{feature}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        )}
      </section>
    </div>
  );
}; 